'use client'

import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { sendChangeColorEvent as productSendChangeColorEvent } from 'product/analytics/sendChangeColorEvent'
import { sendShowSizeSelectorEvent as productSendShowSizeSelectorEvent } from 'product/analytics/sendShowSizeSelectorEvent'
import { GenericEvents as ProductGenericEvents } from 'product/types/GenericEvents'
import { ProductLocations } from 'product/types/ProductLocations'
import { createContext, useContext, useMemo } from 'react'

import { PdpLocations } from '../../analytics/PdpLocations'
import type { CommonProps } from '../../types/CommonProps'
import { GenericEvents } from '../../types/GenericEvents'
import { PdpActionEvents } from '../../types/PdpActionEvents'
import { ScrollToAnchorId } from '../../types/ScrollToAnchor'

export interface AnalyticsContextType {
	sendPdpActionEvent: (action: PdpActionEvents) => void
	sendShowSizeSelectorEvent: () => void
	sendClickSizeEvent: ({ lastUnits }: { lastUnits?: boolean }) => void
	sendChangeColorEvent: () => void
	sendClickSizeGuide: () => void
	sendScrollToAnchorEvent: ({ id }: { id: ScrollToAnchorId }) => void
	sendShowDisclaimerSizeEvent: () => void
}

type AnalyticsProviderProps = Pick<CommonProps, 'product' | 'color'> & {
	children: React.ReactNode
}

export const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
	undefined
)

export function AnalyticsProvider({
	children,
	color,
	product,
}: AnalyticsProviderProps): React.ReactNode {
	const sendPdpActionEvent = (action: string): void => {
		sendGenericEvent(GenericEvents.PDP_ACTION, {
			label_1: action,
			color: color.id,
			product_id: product.id,
		})
	}

	const sendShowSizeSelectorEvent = (): void => {
		productSendShowSizeSelectorEvent({
			colorId: color.id,
			productId: product.id,
			lastUnits: !!color.lastUnits,
			location: ProductLocations.Main,
		})
	}

	const sendClickSizeEvent = ({ lastUnits }: { lastUnits?: boolean }): void => {
		sendGenericEvent(GenericEvents.CLICK_SIZE, {
			last_units: !!lastUnits,
			location: PdpLocations.Main,
		})
	}

	const sendChangeColorEvent = () => {
		productSendChangeColorEvent({
			eventName: ProductGenericEvents.PDP_ACTION,
			colorId: color.id,
			productId: product.id,
			location: ProductLocations.Main,
		})
	}

	const sendClickSizeGuide = (): void => {
		sendGenericEvent(GenericEvents.CLICK_SIZE_GUIDE, {
			location: PdpLocations.Main,
		})
	}

	const sendScrollToAnchorEvent = ({ id }: { id: ScrollToAnchorId }): void => {
		if (id === ScrollToAnchorId.PDP_DESCRIPTION) {
			sendPdpActionEvent(PdpActionEvents.PRODUCT_DESCRIPTION)
		}
	}

	const sendShowDisclaimerSizeEvent = () => {
		sendGenericEvent(GenericEvents.SHOW_DISCLAIMER_SIZE)
	}

	const value = useMemo(
		() => ({
			sendPdpActionEvent,
			sendShowSizeSelectorEvent,
			sendClickSizeEvent,
			sendChangeColorEvent,
			sendClickSizeGuide,
			sendScrollToAnchorEvent,
			sendShowDisclaimerSizeEvent,
		}),
		[color, product]
	)

	return (
		<AnalyticsContext.Provider value={value}>
			{children}
		</AnalyticsContext.Provider>
	)
}

/** @throws {Error} If useAnalytics is used without AnalyticsProvider */
export const useAnalytics = (): AnalyticsContextType => {
	const context = useContext(AnalyticsContext)

	if (!context) {
		throw new Error('useAnalytics must be used within a AnalyticsProvider')
	}

	return context
}
